import { createContext, useContext } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { GridRowProps, RowPropsOverrides } from "@mui/x-data-grid-pro";
import {
  DataGridRowLink,
  useRowLink,
} from "@components/common/data-grid/data-grid-row-link";
import {
  DataGridCustomRow,
  NoRowsOverlay,
  NoRowsOverlayProps,
} from "@components/common/data-grid/adm-data-grid-utils";
import { DataGridToolbarProps } from "@components/common/data-grid/adm-data-grid-types";
import { DataGridToolbar } from "@components/common/data-grid/toolbar/data-grid-toolbar";

/** The props of the custom slots that the grid renders for a table */
interface AdmDataGridSlotProps {
  /** Props of the custom toolbar */
  toolbar: DataGridToolbarProps;

  /** Props of the overlay shown when the table has no rows */
  noRowsOverlay: NoRowsOverlayProps;
}

const AdmDataGridSlotPropsContext = createContext<
  AdmDataGridSlotProps | undefined
>(undefined);

/** Provides the props of the custom slots, which the grid itself cannot type */
export const AdmDataGridSlotPropsProvider =
  AdmDataGridSlotPropsContext.Provider;

function useAdmDataGridSlotProps(): AdmDataGridSlotProps {
  const slotProps = useContext(AdmDataGridSlotPropsContext);

  if (!slotProps) {
    throw new Error("The data grid slots can only be rendered by AdmDataGrid");
  }

  return slotProps;
}

function ToolbarSlot(): React.JSX.Element {
  return <DataGridToolbar {...useAdmDataGridSlotProps().toolbar} />;
}

function NoRowsOverlaySlot(): React.JSX.Element {
  return <NoRowsOverlay {...useAdmDataGridSlotProps().noRowsOverlay} />;
}

/** Renders the row as a link when the table gives it one */
function RowSlot(props: GridRowProps & RowPropsOverrides): React.JSX.Element {
  const link = useRowLink(props.rowId);
  const row = <DataGridCustomRow {...props} />;

  return link === undefined ? (
    row
  ) : (
    <DataGridRowLink to={link}>{row}</DataGridRowLink>
  );
}

function DetailPanelExpandIcon(): React.JSX.Element {
  return <KeyboardArrowDownIcon />;
}

function DetailPanelCollapseIcon(): React.JSX.Element {
  return <KeyboardArrowUpIcon />;
}

/**
 * The slots of the grid, defined once so that they keep a stable identity. Passing them inline
 * would make React unmount and remount them on every render of the grid, which among other things
 * steals the focus back to the search field whenever the table changes.
 */
export const admDataGridSlots = {
  /**
   * We use a custom row to control the visibility of some elements on hover. Every item in the row
   * where visibility = hidden and class name ${SHOW_ON_HOVER_CLASS}, it will be shown on hover.
   * This approach is better than using the recommended hoverId state, because it is faster and
   * reduces the amounts of table rerenders. It also renders the rows of tables that link them as links.
   */
  row: RowSlot,
  toolbar: ToolbarSlot,
  noRowsOverlay: NoRowsOverlaySlot,
  detailPanelExpandIcon: DetailPanelExpandIcon,
  detailPanelCollapseIcon: DetailPanelCollapseIcon,
};
